import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../auth/useAuth";

interface SideNavProps {
  onNavigate?: (page: string) => void;
  currentPage?: string;
}

type MenuIcon = "leagues" | "races" | "ranking" | "people";

function NavigationIcon({ icon }: { icon: MenuIcon }) {
  const paths: Record<MenuIcon, React.ReactNode> = {
    leagues: <><path d="M8 3h8v5a4 4 0 0 1-8 0V3Z" /><path d="M8 5H5v2a4 4 0 0 0 4 4m7-6h3v2a4 4 0 0 1-4 4M12 12v6m-4 3h8m-7-3h6" /></>,
    races: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M7 3v4m10-4v4M3 11h18m-13 5h2m4 0h2" /></>,
    ranking: <><path d="M4 21v-8h5v8m1 0V4h5v17m1 0V9h5v12M2 21h20" /></>,
    people: <><circle cx="9" cy="7" r="3" /><path d="M3 21v-3a6 6 0 0 1 12 0v3m2-10a3 3 0 1 0-1-6m2 10a5 5 0 0 1 3 4v2" /></>,
  };
  return <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 shrink-0">{paths[icon]}</svg>;
}

const competitionLinks: { label: string; path: string; icon: MenuIcon }[] = [
  { label: "Campionati", path: "/leagues", icon: "leagues" },
  { label: "Gare", path: "/dashboard", icon: "races" },
  { label: "Classifiche", path: "/classifiche", icon: "ranking" },
];

const SideNav: React.FC<SideNavProps> = () => {
  const { userInfo, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutError, setLogoutError] = useState("");

  const menuLink = (path: string, label: string, icon: MenuIcon) => (
    <NavLink key={path} to={path} end onClick={() => setMobileOpen(false)}
      className={({ isActive }) => `flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
        isActive ? "bg-brand-100 font-semibold text-brand-800" : "text-slate-700 hover:bg-slate-200/60 hover:text-slate-900"}`}>
      <NavigationIcon icon={icon} /><span>{label}</span>
    </NavLink>
  );

  return (
    <aside aria-label="Gestione" className="management-sidebar flex h-full flex-col border-b border-slate-200 md:border-b-0 md:border-r">
      <div className="flex items-center justify-between gap-3 px-5 py-4 md:py-6">
        <Link to="/" className="flex items-center gap-3 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
          <img src="/images/logo.png" alt="" className="h-11 w-11 object-contain" />
          <span><span className="block text-sm font-semibold text-slate-900">Parco della Sciola</span>
            <span className="block text-xs text-slate-500">Gestione</span></span>
        </Link>
        <button type="button" aria-expanded={mobileOpen} aria-controls="management-menu"
          onClick={() => setMobileOpen(previous => !previous)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 md:hidden">
          {mobileOpen ? "Chiudi menu" : "Menu"}
        </button>
      </div>

      <div id="management-menu" className={`${mobileOpen ? "flex" : "hidden"} min-h-0 flex-1 flex-col md:flex`}>
        <nav aria-label="Navigazione gestione" className="flex-1 space-y-5 overflow-y-auto px-3 pb-5">
          {[
            { title: "Trout Area", prefix: "", id: "trout-menu" },
            { title: "Carpe e Carassi", prefix: "/carpe", id: "carp-menu" },
          ].map(section => <section key={section.id} aria-labelledby={section.id}>
            <h2 id={section.id} className="mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{section.title}</h2>
            <div className="space-y-1">{competitionLinks.map(item => menuLink(`${section.prefix}${item.path}`, item.label, item.icon))}</div>
          </section>)}
          <section aria-labelledby="registry-menu" className="border-t border-slate-200 pt-4">
            <h2 id="registry-menu" className="mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Anagrafica</h2>
            {menuLink("/users", "Concorrenti", "people")}
          </section>
        </nav>

        <div className="border-t border-slate-200 px-5 py-4">
          {userInfo && <p className="mb-2 truncate text-xs text-slate-500">{userInfo.displayName || userInfo.givenName || "Utente"}</p>}
          <button type="button" onClick={async () => {
            setLogoutError("");
            try { await logout(); }
            catch { setLogoutError("Impossibile uscire. Riprova."); }
          }} className="rounded py-1 text-sm font-medium text-slate-600 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
            Esci
          </button>
          {logoutError && <p role="alert" className="mt-2 text-xs text-red-700">{logoutError}</p>}
        </div>
      </div>
    </aside>
  );
};

export default SideNav;
